/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { loader } from "@monaco-editor/react";
import noop from "lodash/noop";
import React, { useEffect, useState, Suspense } from "react";

const Editor = React.lazy(() => import("@monaco-editor/react")); // has window dependency

interface HtmlEditorProps {
  value?: string;
  onChange?: (data: string) => void;
  onError?: (err: any) => void;
  className?: string;
  readOnly?: boolean;
}

export default function CodeEditor(props: HtmlEditorProps) {
  const { value, onChange = noop, onError = noop, className, readOnly } = props;
  const [errors, setErrors] = useState([]);
  const isSSR = typeof window === "undefined";

  const onValidate = (lines) => {
    const errors = lines.map((line) => line.message);
    setErrors(errors);
  };

  useEffect(() => {
    onError(!!errors?.length);
  }, [errors]);

  useEffect(() => {
    loader.init().then((monaco) => {
      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES6,
        allowNonTsExtensions: true,
        allowJs: true,
        noImplicitAny: false,
      });
    });
  }, []);

  return isSSR ? null : (
    <Suspense fallback={<CircularProgress />}>
      <Editor
        className={`marapp-qa-js-editor ${className}`}
        height="50vh"
        theme="vs-dark"
        value={value}
        onChange={onChange}
        onValidate={onValidate}
        language="javascript"
        options={{
          readOnly: !!readOnly,
        }}
      />

      {errors?.length && !readOnly ? (
        <Box mt={2}>
          {errors.map((error, index) => (
            <Typography
              key={index}
              variant="body2"
              color="error"
              className="marapp-qa-error"
              paragraph={true}
            >
              {error}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Suspense>
  );
}
